import { Check, ChevronRight, Percent, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface TipSelectionProps {
    /** The base shown to the guest, in dollars — used for the custom-tip cap and the % readout. */
    subtotal: number;
    /**
     * The tip, in whole cents, a percentage offers — the SAME function the
     * parent charges with (`presetTipCents` on checkout, `percentageTipCents`
     * on pay-at-table). This component never does tip arithmetic of its own,
     * so a tile cannot show a cent the charge does not take.
     */
    tipCentsFor: (percentage: number) => number;
    selectedTip: number;
    customTip: string;
    onTipChange: (percentage: number) => void;
    onCustomTipChange: (amountInDollars: number) => void;
}

export default function TipSelection({
    subtotal,
    tipCentsFor,
    selectedTip,
    customTip,
    onTipChange,
    onCustomTipChange,
}: TipSelectionProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customAmount, setCustomAmount] = useState('');
    const [customPercent, setCustomPercent] = useState('');
    const [customOnlyMode, setCustomOnlyMode] = useState(false);
    // The post-close reset waits out the modal's exit animation. It is cleared by
    // every open, so a reopen inside the 300 ms keeps the mode it opened in, and on
    // unmount, so it never fires into a torn-down page.
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearResetTimer = () => {
        if (resetTimerRef.current !== null) {
            clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        }
    };
    useEffect(() => clearResetTimer, []);

    // The amount shown is the amount charged: the parent's `tipCentsFor`.
    const presetTipDollars = (percentage: number) => tipCentsFor(percentage) / 100;

    const tipOptions = [
        { percentage: 10, amount: presetTipDollars(10) },
        { percentage: 15, amount: presetTipDollars(15) },
        { percentage: 20, amount: presetTipDollars(20) },
    ];

    const selectedTipAmount = customTip
        ? (parseFloat(customTip) || 0)
        : (selectedTip > 0 ? presetTipDollars(selectedTip) : 0);

    // The custom panel's two inputs are one value: "$ amount = percent".
    // A typed PERCENT is priced by `tipCentsFor` — the same derivation as a
    // preset tile — and fills the amount, which is what Done commits. A typed
    // AMOUNT only updates the percent readout; it is charged as typed.
    const handleCustomPercentChange = (value: string) => {
        setCustomPercent(value);
        const percent = Number.parseFloat(value);
        if (value.trim() === '' || !Number.isFinite(percent) || percent <= 0) {
            setCustomAmount('');
            return;
        }
        setCustomAmount((tipCentsFor(percent) / 100).toFixed(2));
    };

    const handleCustomAmountChange = (value: string) => {
        setCustomAmount(value);
        const amount = Number.parseFloat(value);
        setCustomPercent(
            Number.isFinite(amount) && amount >= 0 && subtotal > 0
                ? String(Math.round((amount / subtotal) * 1000) / 10)
                : '',
        );
    };

    const handleCustomClick = () => {
        setShowCustomInput(true);
    };

    const handleTipSelect = (percentage: number) => {
        setShowCustomInput(false);
        onTipChange(percentage);
    };

    const handleModalClose = () => {
        // Propagate validated custom tip to parent before closing
        if (showCustomInput || customOnlyMode) {
            const parsed = parseFloat(customAmount);
            if (!isNaN(parsed) && parsed >= 0) {
                // Clamp to the server cap: max(subtotal, $100)
                const serverCap = Math.max(subtotal, 100);
                onCustomTipChange(Math.min(parsed, serverCap));
            }
        }
        setIsModalOpen(false);
        // Reset to tip selection view when closing
        clearResetTimer();
        resetTimerRef.current = setTimeout(() => {
            resetTimerRef.current = null;
            setShowCustomInput(false);
            setCustomOnlyMode(false);
        }, 300);
    };

    const handleModalOpen = () => {
        clearResetTimer();
        setIsModalOpen(true);
        setShowCustomInput(false);
        setCustomOnlyMode(false);
    };

    const handleCustomModalOpen = () => {
        clearResetTimer();
        setIsModalOpen(true);
        setShowCustomInput(true);
        setCustomOnlyMode(true);
    };

    return (
        <>
            {/* Desktop - Full Section */}
            <div className="mb-8 hidden lg:block">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Tip</h2>
                <div className="flex flex-wrap gap-3">
                    {tipOptions.map((option) => (
                        <button
                            key={option.percentage}
                            onClick={() => onTipChange(option.percentage)}
                            className={`flex-1 min-w-[100px] px-6 py-4 rounded-xl border-2 transition-all ${selectedTip === option.percentage && !customTip
                                ? 'border-gray-900 bg-white'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-left">
                                    <div className="text-sm font-semibold text-gray-900">
                                        ${option.amount.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-600">{option.percentage}%</div>
                                </div>
                                {selectedTip === option.percentage && !customTip && (
                                    <Check className="h-5 w-5 text-gray-900" />
                                )}
                            </div>
                        </button>
                    ))}
                    <button
                        onClick={handleCustomModalOpen}
                        className={`flex-1 min-w-[100px] px-6 py-4 rounded-xl border-2 transition-all ${customTip
                            ? 'border-gray-900 bg-white'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                    >
                        <div className="text-sm font-semibold text-gray-900">Custom</div>
                    </button>
                </div>
            </div>

            {/* Mobile/Tablet - Clickable Card */}
            <button
                onClick={handleModalOpen}
                className="lg:hidden flex items-center justify-between w-full p-4 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all mb-4"
            >
                <div className="flex items-center gap-3">
                    <Percent className="h-5 w-5 text-gray-700" />
                    <span className="font-semibold text-gray-900">Tip</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-gray-900">
                        {customTip ? `Custom $${selectedTipAmount.toFixed(2)}` : `${selectedTip}% $${selectedTipAmount.toFixed(2)}`}
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                </div>
            </button>

            {/* Modal/Bottom Sheet */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 md:flex md:items-center md:justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={handleModalClose}
                    />

                    {/* Modal Content - Bottom sheet on mobile, centered modal on tablet */}
                    <div className="absolute inset-x-0 bottom-0 md:relative md:inset-auto bg-white md:rounded-2xl rounded-t-2xl max-h-[85vh] md:max-h-[80vh] overflow-hidden flex flex-col md:w-[480px] md:animate-none animate-slide-up">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">
                                {customOnlyMode ? 'Custom tip' : 'Add tip'}
                            </h2>
                            <button
                                type="button"
                                onClick={handleModalClose}
                                aria-label="Close"
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors pointer-coarse:p-3"
                            >
                                <X className="h-5 w-5 text-gray-600" aria-hidden />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="space-y-3">
                                {/* Preset Tip Options - Hidden in custom-only mode */}
                                {!customOnlyMode && tipOptions.map((option) => (
                                    <button
                                        key={option.percentage}
                                        onClick={() => handleTipSelect(option.percentage)}
                                        className="w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:border-gray-300"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div
                                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedTip === option.percentage && !showCustomInput
                                                    ? 'border-gray-900 bg-gray-900'
                                                    : 'border-gray-300'
                                                    }`}
                                            >
                                                {selectedTip === option.percentage && !showCustomInput && (
                                                    <div className="w-3 h-3 bg-white rounded-full" />
                                                )}
                                            </div>
                                            <span className="text-lg font-semibold text-gray-900">
                                                {option.percentage}%
                                            </span>
                                        </div>
                                        <span className="text-lg font-semibold text-gray-900">
                                            ${option.amount.toFixed(2)}
                                        </span>
                                    </button>
                                ))}

                                {/* Other Option with Expanded Inputs - Hidden in custom-only mode */}
                                {!customOnlyMode && (
                                    <button
                                        onClick={handleCustomClick}
                                        className="w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:border-gray-300"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div
                                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${showCustomInput
                                                    ? 'border-gray-900 bg-gray-900'
                                                    : 'border-gray-300'
                                                    }`}
                                            >
                                                {showCustomInput && (
                                                    <div className="w-3 h-3 bg-white rounded-full" />
                                                )}
                                            </div>
                                            <span className="text-lg font-semibold text-gray-900">Other</span>
                                        </div>
                                    </button>
                                )}

                                {/* Custom Tip Input Fields - Always visible when Other is selected OR in custom-only mode */}
                                {(showCustomInput || customOnlyMode) && (
                                    <div className="pt-2 space-y-4">
                                        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                                            {/* Amount Input */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Amount
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-900 text-lg font-semibold">
                                                        $
                                                    </span>
                                                    <input
                                                        type="number"
                                                        value={customAmount}
                                                        onChange={(e) => handleCustomAmountChange(e.target.value)}
                                                        placeholder="0.00"
                                                        className="w-full pl-10 pr-4 py-4 rounded-xl border-2 border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900 text-lg placeholder-gray-400"
                                                    />
                                                </div>
                                            </div>

                                            {/* Equals Sign */}
                                            <div className="text-gray-600 font-medium text-2xl pt-8">=</div>

                                            {/* Percent Input */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Percent
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        value={customPercent}
                                                        onChange={(e) => handleCustomPercentChange(e.target.value)}
                                                        placeholder="0"
                                                        className="w-full pr-10 pl-4 py-4 rounded-xl border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900 text-lg placeholder-gray-400"
                                                    />
                                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-900 text-lg font-semibold">
                                                        %
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer Button */}
                        <div className="p-6 border-t border-gray-200">
                            <button
                                onClick={handleModalClose}
                                className="w-full bg-red-700 hover:bg-red-800 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <span>Done</span>
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

